import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Eye, MousePointerClick, TrendingUp } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { StatsCard } from '../components/StatsCard';
import { COLORS } from '../../theme/colors';

export function BlogAnalyticsPage() {
  const [articleStats, setArticleStats] = useState([]);
  const [dailyViews, setDailyViews] = useState([]);
  const [ctaStats, setCtaStats] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  useEffect(() => {
    loadDailyViews();
  }, [selectedSlug, period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [articles, daily, cta] = await Promise.all([
        adminApi.getBlogViewStats(period),
        adminApi.getDailyBlogViews(null, period),
        adminApi.getCtaClickStats(period),
      ]);
      setArticleStats(articles);
      setDailyViews(daily);
      setCtaStats(cta);
    } catch (error) {
      console.error('Failed to load blog analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyViews = async () => {
    try {
      const daily = await adminApi.getDailyBlogViews(selectedSlug, period);
      setDailyViews(daily);
    } catch (error) {
      console.error('Failed to load daily views:', error);
    }
  };

  const totalViews = articleStats.reduce((s, a) => s + a.total_views, 0);
  const totalUnique = articleStats.reduce((s, a) => s + a.unique_views, 0);
  const totalCtaClicks = articleStats.reduce((s, a) => s + a.cta_clicks, 0);
  const avgConversion = totalUnique > 0 ? ((totalCtaClicks / totalUnique) * 100).toFixed(1) : '0.0';

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Blog Analytics</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Only in-browser views (article visible 3s+). Older server-side counts, which included crawlers, are not shown.
          </p>
        </div>
        <select
          className="admin-input"
          value={period}
          onChange={(e) => { setPeriod(Number(e.target.value)); setSelectedSlug(null); }}
          style={{ width: 160 }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="stats-grid">
        <StatsCard title="Total Views" value={totalViews} icon={Eye} color={COLORS.chart1} />
        <StatsCard title="Unique Visitors" value={totalUnique} icon={Eye} color={COLORS.chart2} />
        <StatsCard title="CTA Clicks" value={totalCtaClicks} icon={MousePointerClick} color={COLORS.chart3} />
        <StatsCard title="Avg Conversion" value={`${avgConversion}%`} icon={TrendingUp} color={COLORS.chart4} />
      </div>

      {/* Daily views chart */}
      <div className="chart-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="chart-title" style={{ margin: 0 }}>
            Daily Views {selectedSlug && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>- {selectedSlug}</span>}
          </div>
          {selectedSlug && (
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setSelectedSlug(null)}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Show all
            </button>
          )}
        </div>
        {dailyViews.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyViews}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="total_views" stroke={COLORS.chart1} strokeWidth={2} name="Views" />
              <Line type="monotone" dataKey="unique_views" stroke={COLORS.chart2} strokeWidth={2} name="Unique" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>No view data yet</div>
        )}
      </div>

      {/* Articles table */}
      <div className="admin-card" style={{ padding: 0, marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="chart-title" style={{ margin: 0 }}>Articles ({articleStats.length})</div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Article</th>
              <th style={{ width: 100 }}>Views</th>
              <th style={{ width: 100 }}>Unique</th>
              <th style={{ width: 100 }}>CTA Clicks</th>
              <th style={{ width: 120 }}>Conversion</th>
            </tr>
          </thead>
          <tbody>
            {articleStats.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No data yet</td>
              </tr>
            ) : (
              articleStats.map((a) => (
                <tr key={a.slug}>
                  <td>
                    <button
                      onClick={() => setSelectedSlug(a.slug === selectedSlug ? null : a.slug)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: a.slug === selectedSlug ? 'var(--brand-500)' : 'var(--ink-800)',
                        fontWeight: a.slug === selectedSlug ? 600 : 400,
                        padding: 0,
                        textAlign: 'left',
                        fontSize: 14,
                      }}
                      title="Click to filter chart"
                    >
                      {a.slug}
                    </button>
                  </td>
                  <td>{a.total_views}</td>
                  <td>{a.unique_views}</td>
                  <td>{a.cta_clicks}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 60,
                        height: 6,
                        backgroundColor: 'var(--border)',
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${Math.min(a.conversion_rate * 100, 100)}%`,
                          height: '100%',
                          backgroundColor: a.conversion_rate > 0.05 ? 'var(--success)' : a.conversion_rate > 0 ? 'var(--warning)' : 'var(--border)',
                          borderRadius: 3,
                        }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {(a.conversion_rate * 100).toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* CTA breakdown */}
      {ctaStats.length > 0 && (
        <div className="chart-container">
          <div className="chart-title">CTA Clicks by Article</div>
          <ResponsiveContainer width="100%" height={Math.max(200, ctaStats.length * 35)}>
            <BarChart data={ctaStats} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="slug" type="category" width={200} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="clicks" fill={COLORS.chart1} name="Clicks" />
              <Bar dataKey="unique_visitors" fill={COLORS.chart2} name="Unique" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
